import React, { useEffect, useState, useRef } from "react";
import { SignalPulse } from "../components/SignalPulse";
import { useNavigate } from "react-router-dom";
import { Flame, Loader2, Plus, Trash2, Eye, Globe, Lock, Upload, ExternalLink, Copy, Sparkles, ShieldCheck, HardDrive, Palette, Video, Users, DollarSign, Check, ChevronUp, ChevronDown, Download, Pencil, Link2, GripVertical } from "lucide-react";
import api from "../lib/api";
import { BeaconMedia } from "./BeaconMedia";
import { Avatar } from "../components/Media";
import { useCrypto } from "../context/CryptoContext";
import * as C from "../lib/crypto";
import { encryptAndUploadWithKey, compressImage } from "../lib/mediaUpload";
import { ArtworkFields } from "../components/ArtworkFields";
import { ArtworkStoryFields } from "../components/ArtworkStoryFields";
import { WatermarkFields } from "../components/WatermarkFields";
import { applyWatermark } from "../lib/watermark";
import { parseArt, EMPTY_ART, ART_QUALITY, SECTION_LAYOUTS } from "../lib/artwork";
import { LEVELS } from "../lib/learn";
import { TeacherInsights } from "../components/TeacherInsights";
import { BeaconHosting } from "../components/BeaconHosting";
import { BeaconHearthPanel } from "../components/BeaconHearthPanel";
import { BeaconSubscriptions } from "../components/BeaconSubscriptions";
import { beaconLook, bannerSize } from "../lib/beaconStyles";
import { BeaconHero } from "../components/BeaconHero";
import { BeaconStylePanel } from "../components/BeaconStylePanel";
import { BeaconTemplatePicker } from "../components/BeaconTemplatePicker";
import { GuideWelcome } from "../components/GuideWelcome";
import { themeClass } from "../lib/beaconTemplates";
import { BeaconContentBoard } from "../components/BeaconContentBoard";
import { BeaconWelcomeEditor } from "../components/BeaconWelcomeEditor";
import { BeaconDashboard } from "../components/BeaconDashboard";
import { BeaconMilestone } from "../components/BeaconMilestone";
import { BeaconDisclaimerEditor, DEFAULT_DISCLAIMER } from "../components/BeaconDisclaimer";
import { CoachTips, useTips } from "../components/CoachTip";
import { useAuth } from "../context/AuthContext";
import { SectionCommentToggle, ItemCommentToggle } from "../components/BeaconCommentToggles";
import { BeaconGuide } from "../components/BeaconGuide";
import { ShareToHearth } from "../components/ShareToHearth";
import { DripFields, dripLabel } from "../components/DripFields";
import { BlueprintDownload } from "../components/BlueprintDownload";
import { PROTON_DRIVE_AFFILIATE_URL } from "../lib/proton";

const ITEM_TYPES = [["text", "Written lesson"], ["video", "Video"], ["audio", "Audio"], ["image", "Art / image"], ["artwork", "Artwork (gallery piece)"], ["session", "Live session"], ["link", "Website link"], ["file", "Download (PDF, worksheet)"]];
const MIME_FOR = { artwork: "image/jpeg", image: "image/jpeg", video: "video/mp4", audio: "audio/mpeg" };

// Owner's roster of who has joined, with join date + encryption-access status.
const BeaconSubscribersPanel = ({ accent }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let live = true;
    const fetchOnce = () => api.get("/beacon/mine/subscribers").then((r) => { if (live) setData(r.data); }).catch(() => { if (live) setData((p) => p || { subscribers: [], count: 0, waiting: 0 }); });
    fetchOnce();
    // The dashboard auto-grants pending keys on load; re-check shortly so the
    // "waiting" badges clear without needing a manual refresh.
    const t = setTimeout(fetchOnce, 6000);
    return () => { live = false; clearTimeout(t); };
  }, []);
  if (!data) return null;
  const subs = data.subscribers || [];
  return (
    <div className="border border-white/10 bg-[#121212] p-5 mb-6" data-testid="beacon-subscribers-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-body text-lg text-white flex items-center gap-2"><Users size={16} style={{ color: accent }} /> Your subscribers</h2>
        {data.waiting > 0 && (
          <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-amber-400 border border-amber-400/40 px-2 py-1 rounded" data-testid="beacon-waiting-count">
            <Loader2 size={11} className="animate-spin" /> {data.waiting} waiting for access
          </span>
        )}
      </div>
      <p className="font-body text-xs text-zinc-500 mb-4">Everyone who has joined your Beacon. {data.enc_enabled ? "Anyone marked \"waiting\" will unlock automatically now that you've opened your dashboard, no action needed." : "Turn on encrypted teachings and access is granted automatically."}</p>
      {subs.length === 0 ? (
        <p className="font-body text-sm text-zinc-600 py-4 text-center">No subscribers yet. Share your Beacon link to bring people in.</p>
      ) : (
        <div className="space-y-2" data-testid="beacon-subscribers-list">
          {subs.map((s) => (
            <div key={s.id} className="flex items-center gap-3 py-2 border-t border-white/5" data-testid={`beacon-subscriber-${s.id}`}>
              <Avatar name={s.name} src={s.avatar} iv={s.avatar_iv} size={34} />
              <div className="min-w-0 flex-1">
                <div className="font-body text-sm text-zinc-200 truncate">{s.name || "A sovereign"}{s.paid && <span className="ml-2 align-middle text-[10px] uppercase tracking-wider text-[#14A89A] border border-[#14A89A]/40 px-1.5 py-0.5" data-testid={`beacon-subscriber-paid-${s.id}`}>Paid</span>}</div>
                {s.since && <div className="font-body text-[11px] text-zinc-600">Joined {new Date(s.since).toLocaleDateString()}</div>}
              </div>
              {s.waiting
                ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-400/90 shrink-0"><Loader2 size={10} className="animate-spin" /> Preparing access</span>
                : <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#14A89A] shrink-0"><Check size={11} /> {data.enc_enabled ? "Has access" : "Member"}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, ...p }) => (
  <label className="block mb-3">
    <span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500">{label}</span>
    <input {...p} className="mt-1 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 font-body text-sm text-white focus:border-[#14A89A] outline-none" />
  </label>
);

export default function Beacon() {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const { ensureMyBeaconKey, grantMyBeaconKeyToPending, status: cryptoStatus } = useCrypto();
  const [beacon, setBeacon] = useState(undefined);
  const [beaconKey, setBeaconKey] = useState(null);
  const [securing, setSecuring] = useState(false);
  const migratedRef = useRef(false);
  const [dir, setDir] = useState([]);
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});
  const [newSection, setNewSection] = useState("");
  const [dirty, setDirty] = useState(false);
  const [checklistDismissed, setChecklistDismissed] = useState(() => localStorage.getItem("beacon_checklist_dismissed") === "1");

  // Update a profile field and mark the form as having unsaved changes.
  const editVer = useRef(0);
  const [saveState, setSaveState] = useState("idle");
  const [saveErr, setSaveErr] = useState("");
  const updateForm = (patch) => { editVer.current += 1; setForm((f) => ({ ...f, ...patch })); setDirty(true); };

  // keepForm=true refreshes content (sections/items) WITHOUT overwriting unsaved profile edits.
  const load = async ({ keepForm = false } = {}) => {
    const { data } = await api.get("/beacon/mine");
    setBeacon(data.beacon);
    if (data.beacon && !keepForm) {
      setForm({
        title: data.beacon.title || "", handle: data.beacon.handle || "", tagline: data.beacon.tagline || "",
        about: data.beacon.about || "", price_monthly: data.beacon.price_monthly || 0,
        accent_color: data.beacon.accent_color || "#14A89A", public_teaser_enabled: !!data.beacon.public_teaser_enabled,
        teaser_show_name: !!data.beacon.teaser_show_name,
        heading_font: data.beacon.heading_font || "cabinet", heading_color: data.beacon.heading_color || "#FFFFFF",
        cover_media: data.beacon.cover_media || "", welcome_video: data.beacon.welcome_video || "",
        tagline_font: data.beacon.tagline_font || "", tagline_color: data.beacon.tagline_color || "",
        section_font: data.beacon.section_font || "", section_color: data.beacon.section_color || "",
        section_size: data.beacon.section_size || "md", banner_size: data.beacon.banner_size || "standard", title_position: data.beacon.title_position || "below",
        welcome_message: data.beacon.welcome_message || "", welcome_teaser: !!data.beacon.welcome_teaser,
        disclaimer_enabled: data.beacon.disclaimer_enabled !== false, disclaimer_text: data.beacon.disclaimer_text ?? DEFAULT_DISCLAIMER,
        page_theme: data.beacon.page_theme || "dark", template: data.beacon.template || null,
      });
      setDirty(false);
    }
    try { const { data: d } = await api.get("/beacon/directory"); setDir(d); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  // Zero-knowledge Beacon key: ensure it exists, hand it to any waiting subscribers,
  // and quietly encrypt any legacy plaintext teachings the first time we load.
  // Gated on the crypto vault being unlocked so the identity key is available.
  useEffect(() => {
    if (!beacon || !beacon.id) return;
    if (!(beacon.status === "approved" || beacon.status === "live")) return;
    if (cryptoStatus !== "unlocked") return;
    let cancel = false;
    (async () => {
      let key = null;
      for (let attempt = 0; attempt < 3 && !key && !cancel; attempt++) {
        try {
          key = await ensureMyBeaconKey(beacon.id);
        } catch (e) {
          console.error("Beacon key setup failed (attempt " + (attempt + 1) + ")", e);
          await new Promise((r) => setTimeout(r, 700));
        }
      }
      if (cancel || !key) return;
      setBeaconKey(key);
      try { await grantMyBeaconKeyToPending(beacon.id); } catch (e) { console.error("Beacon key grant failed", e); }
      if (!migratedRef.current) { migratedRef.current = true; try { await migratePlaintext(key); } catch (e) { console.error("Beacon migrate failed", e); } }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beacon?.id, beacon?.status, cryptoStatus]);

  // Re-encrypt legacy plaintext subscriber items (not preview/public) under the Beacon key.
  const migratePlaintext = async (key) => {
    const secs = beacon?.sections || [];
    const encFn = (buf) => C.encryptBytes(key, buf);
    let changed = false;
    for (const s of secs) {
      for (const it of (s.items || [])) {
        if (it.enc || it.is_public || it.is_preview) continue;
        const hasMedia = ["image", "video", "audio", "artwork", "file"].includes(it.type) && it.media;
        const hasSecret = (["text", "artwork", "file"].includes(it.type) && it.body) || it.caption || hasMedia;
        if (!hasSecret) continue;
        try {
          const payload = { enc: true };
          if (it.caption) { const e = await C.encryptText(key, it.caption); payload.caption = e.ctB64; payload.caption_iv = e.ivB64; }
          if (["text", "artwork", "file"].includes(it.type) && it.body) { const e = await C.encryptText(key, it.body); payload.body = e.ctB64; payload.body_iv = e.ivB64; }
          if (hasMedia) {
            if (!securing) setSecuring(true);
            const r = await api.get(`/files/${it.media}`, { responseType: "blob" });
            const f = new File([r.data], "m", { type: MIME_FOR[it.type] });
            const up = await encryptAndUploadWithKey(f, encFn, it.type);
            payload.media = up.path; payload.media_iv = up.iv || null;
            payload.thumb = up.thumb || null; payload.thumb_iv = up.thumb_iv || null;
          }
          await api.patch(`/beacon/mine/items/${it.id}`, payload);
          changed = true;
        } catch { /* skip this item, leave as-is */ }
      }
    }
    setSecuring(false);
    if (changed) await load({ keepForm: true });
  };

  // Warn before leaving/reloading with unsaved profile changes.
  useEffect(() => {
    const h = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  const submitRequest = async () => {
    if (desc.trim().length < 10) return;
    setBusy(true);
    try { await api.post("/beacon/request", { project_desc: desc }); await load(); } finally { setBusy(false); }
  };
  // Auto-save: persist profile edits ~1s after the creator stops changing things.
  const persist = async (snapshot) => {
    const ver = editVer.current;
    setSaveState("saving");
    try {
      await api.put("/beacon/mine", snapshot);
      if (editVer.current === ver) setDirty(false);
      setSaveState("saved"); setSaveErr("");
      await load({ keepForm: true });
    } catch (e) { setSaveState("error"); setSaveErr(e?.response?.data?.detail || "Couldn't save, check your connection."); }
  };
  useEffect(() => {
    if (!dirty) return;
    setSaveState("pending");
    const t = setTimeout(() => persist(form), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, dirty]);
  const saveSettings = () => persist(form);
  // Inline edit-in-place (title/tagline on the live preview): persists a single
  // field immediately and syncs both the form and the saved baseline.
  const saveField = async (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
    try {
      await api.put("/beacon/mine", { [field]: value });
      setBeacon((b) => (b ? { ...b, [field]: value } : b));
    } catch (e) { window.alert(e?.response?.data?.detail || "Save failed"); }
  };
  const publish = async (live) => {
    try { await api.post("/beacon/mine/publish", { live }); await load({ keepForm: true }); }
    catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't update your Beacon";
      if (e?.response?.status === 403 && /two-step/i.test(msg)) { if (window.confirm(`${msg}\n\nOpen My HQ to set it up now?`)) nav(`/profile/${me.id}`); }
      else window.alert(msg);
    }
  };
  const addSection = async () => { if (!newSection.trim()) return; await api.post("/beacon/mine/sections", { title: newSection }); setNewSection(""); await load({ keepForm: true }); };
  const delSection = async (id) => { if (window.confirm(`Delete this section and everything in it?\n\n${FREE_NOTE}`)) { await api.delete(`/beacon/mine/sections/${id}`); await load({ keepForm: true }); } };
  const delItem = async (id) => { if (!window.confirm(`Delete this item?\n\n${FREE_NOTE}`)) return; await api.delete(`/beacon/mine/items/${id}`); await load({ keepForm: true }); };
  const moveSection = async (id, dir) => { await api.post(`/beacon/mine/sections/${id}/move`, { dir }); await load({ keepForm: true }); };
  const renameSection = async (id, title) => { if (!title.trim()) return; await api.patch(`/beacon/mine/sections/${id}`, { title }); await load({ keepForm: true }); };
  const reorder = async (layout) => {
    try { await api.post("/beacon/mine/reorder", { sections: layout.map((s) => ({ id: s.id, items: s.items.map((it) => it.id) })) }); }
    catch (e) { window.alert(e?.response?.data?.detail || "Couldn't save the new order, please try again."); }
    await load({ keepForm: true });
  };
  const moveItem = async (id, dir) => { await api.post(`/beacon/mine/items/${id}/move`, { dir }); await load({ keepForm: true }); };
  const resetBeacon = async () => {
    if (!window.confirm(`This erases EVERYTHING in your Beacon (all sections, items, and your title, cover, price and settings) and returns it to a blank draft. Your creator status and link handle are kept. This cannot be undone.\n\n${FREE_NOTE}\n\nStart from scratch?`)) return;
    if (!window.confirm("Are you absolutely sure? All your Beacon content will be permanently deleted.")) return;
    setBusy(true);
    try { await api.post("/beacon/mine/reset"); await load(); window.scrollTo(0, 0); } finally { setBusy(false); }
  };

  const downloadMine = () => {
    if (!beacon) return;
    const data = {
      exported_at: new Date().toISOString(),
      title: form.title, tagline: form.tagline, about: form.about,
      handle: form.handle, price_monthly: form.price_monthly,
      accent_color: form.accent_color, heading_font: form.heading_font, heading_color: form.heading_color,
      tagline_font: form.tagline_font, tagline_color: form.tagline_color, section_font: form.section_font,
      section_color: form.section_color, section_size: form.section_size, banner_size: form.banner_size, title_position: form.title_position,
      cover_media: form.cover_media, welcome_video: form.welcome_video, welcome_message: form.welcome_message,
      sections: (beacon.sections || []).map((s) => ({
        title: s.title,
        items: (s.items || []).map((it) => ({ type: it.type, title: it.title, body: it.body, media: it.media, session: it.session, caption: it.caption, link_url: it.link_url, link_label: it.link_label, link_thumb: it.link_thumb, is_preview: it.is_preview, is_public: it.is_public })),
      })),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${form.handle || "my"}-beacon.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if (beacon === undefined) return <div className="flex justify-center py-20"><SignalPulse size={44} /></div>;

  // ---- Not yet a creator: info + request ----
  if (!beacon || beacon.status === "declined") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="beacon-info">
        <div className="flex items-center gap-3 mb-4"><Flame className="text-[#14A89A]" size={30} /><h1 className="font-display text-4xl sm:text-5xl text-white">The Beacon</h1></div>
        <p className="font-body text-lg text-zinc-300 leading-relaxed mb-3">A Beacon Profile is your own teaching &amp; creator platform, living inside Sovereign Signal. Think of it as your private academy, studio, or stage: a beautiful space you fully control, where your work reaches the members who value it most.</p>
        <p className="font-body text-zinc-400 leading-relaxed mb-8">Your everyday profile stays exactly as it is. Your Beacon is a separate, illuminated layer you switch on, and light only as bright as you choose.</p>

        <h2 className="font-body text-xl text-white mb-4">What you get</h2>
        <div className="grid sm:grid-cols-2 gap-3 mb-8">
          {[
            [Video, "Your content, organized", "Group videos, audio, art galleries, written lessons and live sessions into clean, reorderable sections: a whole series or curriculum in one place."],
            [DollarSign, "Get paid directly", "Set your own monthly price, or offer it free. Subscribers pay you directly through Stripe; SovSig never touches or holds your money."],
            [Palette, "Make it yours", "Personalize with your own cover, accent color, tagline, welcome video and about, built to fit an artist, teacher, musician, healer or guide."],
            [Users, "Live sessions", "Schedule encrypted live gatherings (via Proton Meet) for your subscribers: classes, circles, Q&As, performances."],
            [Globe, "A shareable public teaser", "Optionally open a public page with its own link to invite the outside world in, and it carries your personal referral code, so new members are credited to you."],
            [Sparkles, "Grow your standing", "Every person who joins through your Beacon strengthens your presence and your Resonance in the network."],
          ].map(([Icon, title, body], i) => (
            <div key={i} className="border border-white/10 bg-[#121212] p-4">
              <div className="flex items-center gap-2 mb-1.5"><Icon size={16} className="text-[#14A89A] shrink-0" /><span className="font-body text-white font-medium min-w-0">{title}</span></div>
              <p className="font-body text-sm text-zinc-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>

        <div className="border border-[#14A89A]/40 bg-gradient-to-br from-[#14A89A]/10 to-transparent p-5 mb-8" data-testid="beacon-downloadable-callout">
          <div className="flex items-center gap-2 mb-2"><Download size={18} className="text-[#14A89A] shrink-0" /><h3 className="font-body text-white font-medium text-lg min-w-0">Yours to keep: downloadable in one click</h3></div>
          <p className="font-body text-sm text-zinc-300 leading-relaxed">All your work, in one place: <span className="text-white">protected, recoverable, and downloadable with a single click</span>. Export your entire Beacon Profile (every section, lesson, link and setting) as one portable file you own outright. Platforms can vanish overnight; your life's work never does. This is your backup plan, built in.</p>
        </div>

        <h2 className="font-body text-xl text-white mb-3">Simple, honest pricing</h2>
        <div className="border border-[#14A89A]/50 bg-[#14A89A]/[0.08] p-4 mb-3 flex items-start gap-3" data-testid="beacon-free-note">
          <Sparkles size={18} className="text-[#14A89A] shrink-0 mt-0.5" />
          <div>
            <div className="font-body text-white font-semibold">Free while we grow</div>
            <p className="font-body text-sm text-zinc-300 leading-relaxed mt-0.5">Lighting a Beacon costs nothing today, for every creator. Storage hosting isn't charged yet, and every Beacon owner will get an email <span className="text-white">at least 30 days</span> before it begins.</p>
          </div>
        </div>
        <div className="border border-[#14A89A]/30 bg-[#121212] p-5 mb-3">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-2">
            <HardDrive size={18} className="text-[#14A89A] self-center" />
            <span className="font-display text-3xl text-white">$33</span>
            <span className="font-body text-zinc-400">/ month</span>
            <span className="font-body text-sm text-zinc-500 sm:ml-2">· up to 50&nbsp;GB of storage</span>
          </div>
          <p className="font-body text-sm text-zinc-400 leading-relaxed">This will be your flat hosting fee paid to SovSig once hosting begins, covering the data, streaming and infrastructure that keep your Beacon running. That's the only fee you pay us for hosting.</p>
          <div className="mt-3 pt-3 border-t border-white/10 space-y-1.5">
            <p className="font-body text-sm text-zinc-300 flex gap-2"><Check size={15} className="text-[#14A89A] mt-0.5 shrink-0" /><span className="min-w-0">Members pay you <span className="text-white">directly, through your own Stripe account</span>. SovSig takes just a <span className="text-white">1% platform fee</span> on each payment, collected automatically. Stripe's standard card fees apply.</span></p>
            <p className="font-body text-sm text-zinc-300 flex gap-2"><Check size={15} className="text-[#14A89A] mt-0.5 shrink-0" /> You set your own price, or make your Beacon completely free.</p>
            <p className="font-body text-sm text-zinc-300 flex gap-2"><Check size={15} className="text-[#14A89A] mt-0.5 shrink-0" /> Stripe pays you directly and handles taxes, payouts and refunds, SovSig is never in the middle of your money.</p>
          </div>
        </div>
        <div className="border border-amber-400/30 bg-amber-400/[0.04] p-5 mb-8" data-testid="beacon-founding-note">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-2">
            <Sparkles size={15} className="text-amber-300 self-center" />
            <span className="font-body text-white font-medium">Founding creators: 40% off for life</span>
            <span className="font-body text-sm text-zinc-400 sm:ml-auto" data-testid="beacon-founding-price"><span className="line-through text-zinc-500 mr-1.5">$33</span><span className="text-amber-300 font-semibold">$19/mo</span> for 50&nbsp;GB · founding, for life</span>
          </div>
          <p className="font-body text-sm text-zinc-300 leading-relaxed">You pay nothing to light your Beacon. If you charge subscribers, SovSig takes 1% of each payment, and that's it. Storage hosting (normally $33/mo for 50&nbsp;GB) will begin later at your founding price of <span className="text-white">$19/mo, locked in for life</span>. We'll give you <span className="text-white">at least 30 days' notice by email</span> before it starts, and you can always export everything in one click.</p>
        </div>

        <h2 className="font-body text-xl text-white mb-3 flex items-center gap-2"><ShieldCheck size={18} className="text-[#14A89A]" /> Your privacy, in plain words</h2>
        <div className="border border-white/10 bg-[#121212] p-5 mb-8 space-y-3">
          <p className="font-body text-sm text-zinc-300 leading-relaxed"><Lock size={14} className="inline mr-1.5 text-[#14A89A]" /><span className="text-white">Encrypted for your subscribers.</span> Your locked teachings are encrypted on your device with a key unique to your Beacon. The server only ever stores scrambled ciphertext, so we can't read your content, and nobody on the open internet can reach it. It's never public, scraped, or indexed.</p>
          <p className="font-body text-sm text-zinc-300 leading-relaxed"><Lock size={14} className="inline mr-1.5 text-[#14A89A]" /><span className="text-white">Private and access-controlled.</span> Only you and your active subscribers can open locked content. Members who haven't subscribed see only the previews you choose to share.</p>
          <p className="font-body text-sm text-zinc-300 leading-relaxed"><ShieldCheck size={14} className="inline mr-1.5 text-[#14A89A]" /><span className="text-white">The Council doesn't browse it.</span> Because each Beacon has its own key, the Council can't casually read your content. Nothing becomes visible unless a subscriber flags a specific item for a genuine safety concern, and even then it takes <span className="text-white">two independent stewards to co-sign</span> before anything can be unsealed, with every reveal written to a permanent, tamper-evident audit log. It's the same safety channel used across the network, never casual browsing. (For reference, your private profile and 1:1 DMs use our strongest zero-knowledge encryption, which even we can never read.)</p>
          <p className="font-body text-sm text-zinc-300 leading-relaxed"><Globe size={14} className="inline mr-1.5 text-amber-400/80" /><span className="text-white">You choose what's public.</span> A Beacon is private by default. If you turn on a public teaser, only the specific items you mark public leave the encrypted space, and we'll always warn you clearly first.</p>
        </div>

        <h2 className="font-body text-xl text-white mb-2">Ready to light your Beacon?</h2>
        <BlueprintDownload testid="request-blueprint-download" className="mb-4" />
        <p className="font-body text-sm text-zinc-400 mb-4">Tell us a little about your project. The Council reviews each request personally, and we'll be in touch to welcome you in.</p>
        {beacon?.status === "declined" && <p className="font-body text-sm text-amber-400/80 mb-3">Your previous request wasn't approved. You're welcome to refine your project and ask again.</p>}
        <label className="block mb-3"><span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500">Describe your project</span>
          <textarea data-testid="beacon-project-desc" value={desc} onChange={(e) => setDesc(e.target.value)} rows={4} placeholder="What will you teach or share? Who is it for?" className="mt-1 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 font-body text-sm text-white focus:border-[#14A89A] outline-none" />
        </label>
        <button data-testid="beacon-request-btn" disabled={busy || desc.trim().length < 10} onClick={submitRequest} className="flex items-center gap-2 bg-[#14A89A] text-[#0A0A0A] font-body font-semibold px-5 py-2.5 disabled:opacity-40 hover:brightness-110 transition">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Flame size={16} />} Light my Beacon: send to Council
        </button>
        {dir.length > 0 && <BeaconBrowse dir={dir} nav={nav} />}
      </div>
    );
  }

  if (beacon.status === "pending") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="beacon-pending">
        <Flame className="text-[#14A89A] mx-auto mb-4" size={32} />
        <h1 className="font-display text-3xl text-white mb-2">Your Beacon awaits the Council</h1>
        <p className="font-body text-zinc-400">We've received your request and the Council will review it soon. You'll be able to build your space the moment it's approved.</p>
        {dir.length > 0 && <BeaconBrowse dir={dir} nav={nav} />}
      </div>
    );
  }

  // ---- Approved / live: builder ----
  return (
    <div className="max-w-3xl mx-auto px-4 py-8" data-testid="beacon-builder">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3"><Flame className="text-[#14A89A]" /><h1 className="font-display text-3xl text-white">Your Beacon</h1>
          <span className={`font-body text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border ${beacon.status === "live" ? "border-[#14A89A]/50 text-[#14A89A]" : "border-white/15 text-zinc-400"}`}>{beacon.status === "live" ? "Live" : "Draft"}</span>
        </div>
        <div className="flex gap-2">
          <BeaconGuide template={beacon.template} />
          {beacon.status === "live" && beacon.handle && <button onClick={() => nav(`/beacon/view/${beacon.handle}`)} data-testid="beacon-view-btn" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:bg-white/5"><ExternalLink size={14} /> View</button>}
          {beacon.status === "live"
            ? <button onClick={() => publish(false)} data-testid="beacon-unpublish-btn" className="border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:bg-white/5">Unpublish</button>
            : <button onClick={() => publish(true)} data-testid="beacon-publish-btn" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs hover:brightness-110">Go live</button>}
        </div>
      </div>

      <GuideWelcome userId={me?.id} hasTemplate={!!beacon.template} />
      <BeaconTemplatePicker key={beacon.template || "none"} beacon={beacon} updateForm={updateForm} onApplied={() => load({ keepForm: true })} />

      {/* Get-started checklist */}
      {(() => {
        const secs = beacon.sections || [];
        const steps = [
          ["Name your space & pick a link handle", !!(beacon.title && beacon.handle)],
          ["Add a section to hold your work", secs.length > 0],
          ["Add your first item (video, audio, art, lesson or session)", secs.some((s) => (s.items || []).length > 0)],
          ["Optional: turn on your shareable public teaser", !!beacon.public_teaser_enabled],
          ["Go live", beacon.status === "live"],
        ];
        const requiredDone = steps[0][1] && steps[1][1] && steps[2][1] && steps[4][1];
        if (checklistDismissed || requiredDone) return null;
        return (
          <div className="border border-[#14A89A]/30 bg-[#121212] p-5 mb-6" data-testid="beacon-checklist">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-body text-lg text-white flex items-center gap-2"><Sparkles size={17} className="text-[#14A89A]" /> Get started</h2>
              <button onClick={() => { localStorage.setItem("beacon_checklist_dismissed", "1"); setChecklistDismissed(true); }} className="text-zinc-500 hover:text-white text-xs font-body">Dismiss</button>
            </div>
            <div className="space-y-2">
              {steps.map(([label, done], i) => (
                <div key={i} className="flex items-center gap-2.5" data-testid={`beacon-step-${i}`}>
                  <span className={`w-5 h-5 rounded-full border flex items-center justify-center shrink-0 ${done ? "bg-[#14A89A] border-[#14A89A]" : "border-white/25"}`}>{done && <Check size={13} className="text-[#0A0A0A]" />}</span>
                  <span className={`font-body text-sm ${done ? "text-zinc-500 line-through" : "text-zinc-200"}`}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      <BeaconMilestone accent={beacon.accent_color || "#14A89A"} />

      {/* Earnings / reach */}
      <div data-guide="hosting"><BeaconHosting /></div>
      <div data-guide="hearth"><BeaconHearthPanel /></div>
      {/* Who's in, subscriber list with access status */}
      <div data-guide="subscribers"><BeaconSubscribersPanel accent={beacon.accent_color || "#14A89A"} /></div>
      <div data-guide="pricing"><BeaconSubscriptions /></div>
      <div data-guide="dashboard"><BeaconDashboard beacon={beacon} /></div>
      <div className="mt-6" data-guide="insights"><TeacherInsights beacon={beacon} beaconKey={beaconKey} onChange={() => load({ keepForm: true })} /></div>



      {/* Settings */}
      <div className="border border-white/10 bg-[#121212] p-5 mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
          <h2 className="font-body text-lg text-white">Illuminate your space</h2>
          <ShowTipsAgain />
        </div>
        <CoachTips ids={["beacon-preview"]} />

        {/* Personalization: banner, fonts & colours + live preview */}
        <CoachTips ids={["beacon-style"]} />
        <div className="mb-5 border border-white/10 bg-[#0A0A0A] p-4" data-testid="beacon-personalize">
          <BeaconStylePanel form={form} updateForm={updateForm} />
          <div className={`mt-5 p-3 ${themeClass(form.page_theme)}`} data-testid="beacon-style-preview">
            <div className="font-body text-[10px] tracking-[0.15em] uppercase text-zinc-600 mb-2">Live preview: click the title or tagline to edit</div>
            <BeaconHero b={form}
              title={<InlineEdit value={form.title} placeholder="Your title" onSave={(v) => saveField("title", v)} testid="beacon-inline-title"
                className="max-w-full" inputClassName="bg-transparent border-b border-[#14A89A]/50 outline-none w-full min-w-0" />}
              tagline={<InlineEdit value={form.tagline} placeholder="Your tagline goes here" onSave={(v) => saveField("tagline", v)} testid="beacon-inline-tagline"
                className="max-w-full" inputClassName="bg-transparent border-b border-[#14A89A]/50 outline-none w-full min-w-0" />} />
            <div className="font-body text-[10px] tracking-[0.2em] uppercase text-zinc-600 mb-1" data-testid="beacon-preview-section-caption">Section title style · preview only</div>
            <h2 className={`${beaconLook(form).sSize} pb-2 border-b`} style={{ fontFamily: beaconLook(form).sFont, color: beaconLook(form).sColor, borderColor: (form.accent_color || "#14A89A") + "44" }} data-testid="beacon-preview-section-title">{beacon.sections?.[0]?.title || "Your first section"}</h2>
          </div>
        </div>

        <Field label="Title" data-testid="beacon-f-title" value={form.title} onChange={(e) => updateForm({ title: e.target.value })} />
        <Field label="Link handle (your beacon URL)" data-testid="beacon-f-handle" value={form.handle} onChange={(e) => updateForm({ handle: e.target.value })} placeholder="luma-yoga" />
        <Field label="Tagline" data-testid="beacon-f-tagline" value={form.tagline} onChange={(e) => updateForm({ tagline: e.target.value })} />
        <label className="block mb-3"><span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500">About</span>
          <textarea value={form.about} onChange={(e) => updateForm({ about: e.target.value })} rows={3} className="mt-1 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 font-body text-sm text-white focus:border-[#14A89A] outline-none" />
        </label>
        <div className="flex gap-4 flex-wrap items-end">
          <Field label="Monthly price (USD, 0 = free)" data-testid="beacon-f-price" type="number" min="0" max="999" value={form.price_monthly} onChange={(e) => updateForm({ price_monthly: e.target.value })} />
          <label className="block mb-3"><span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500">Accent</span>
            <input type="color" value={form.accent_color} onChange={(e) => updateForm({ accent_color: e.target.value })} className="mt-1 block h-10 w-16 bg-transparent border border-white/10" />
          </label>
        </div>
        <CoverUpload label="Cover banner" value={form.cover_media} ratio={bannerSize(form.banner_size).ratio} onDone={(p) => updateForm({ cover_media: p })} />
        <div data-guide="welcome"><BeaconWelcomeEditor form={form} updateForm={updateForm} beacon={beacon} beaconKey={beaconKey} ensureKey={ensureMyBeaconKey} onSaved={() => load({ keepForm: true })}
          videoUpload={<MediaUpload label="Trailer video: for everyone (optional)" value={form.welcome_video} type="video" onDone={(p) => updateForm({ welcome_video: p })} />} /></div>
        <BeaconDisclaimerEditor form={form} updateForm={updateForm} />
        <label className="flex items-center gap-2 mt-3 mb-4 cursor-pointer" data-testid="beacon-teaser-toggle">
          <input type="checkbox" checked={form.public_teaser_enabled} onChange={(e) => { if (e.target.checked && !window.confirm("A public teaser page is viewable by anyone with the link, it is NOT private/encrypted. Enable it?")) return; updateForm({ public_teaser_enabled: e.target.checked }); }} className="accent-[#14A89A]" />
          <span className="font-body text-sm text-zinc-300"><Globe size={13} className="inline mr-1.5 text-zinc-500" />Enable a shareable public teaser page</span>
        </label>
        {form.public_teaser_enabled && (
          <label className="flex items-start gap-2 -mt-1 mb-4 cursor-pointer pl-1" data-testid="beacon-teaser-name-toggle">
            <input type="checkbox" checked={!!form.teaser_show_name} onChange={(e) => updateForm({ teaser_show_name: e.target.checked })} className="accent-[#14A89A] mt-1" />
            <span className="font-body text-xs text-zinc-400 leading-relaxed">Show my name on my public teaser. <span className="text-zinc-500">Inside Sovereign Signal your profile name is end-to-end encrypted and private, turn this on only if you want it shown publicly on your teaser page (anyone with the link can see it). Left off, the teaser shows only your Beacon's title, tagline, and content.</span></span>
          </label>
        )}
        <SaveStatus state={saveState} error={saveErr} onRetry={saveSettings} />
        {beacon.public_teaser_enabled && beacon.handle && <CopyTeaser handle={beacon.handle} />}
      </div>

      {/* Sections & items */}
      <div className="border border-white/10 bg-[#121212] p-5" data-guide="content">
        <h2 className="font-body text-lg text-white mb-1 flex items-center gap-2">Your content {beaconKey && <span title="Subscriber content is encrypted with your Beacon key, the server can't read it" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#14A89A] border border-[#14A89A]/40 px-1.5 py-0.5 rounded"><Lock size={10} /> Encrypted</span>}</h2>
        <p className="font-body text-xs text-zinc-500 mb-4">Anything that isn't a free preview or public teaser is sealed with your Beacon key, the server can't read it. {securing && <span className="text-[#14A89A]"><Loader2 size={11} className="inline animate-spin mr-1" />Securing your existing content…</span>}</p>
        <CoachTips ids={["beacon-content"]} />
        <CoachTips ids={["beacon-visibility"]} />
        {(beacon.sections || []).length > 0 && <CoachTips ids={["beacon-comments"]} />}
        <div className="flex gap-2 mb-5">
          <input value={newSection} onChange={(e) => setNewSection(e.target.value)} placeholder="New section title" data-testid="beacon-new-section" className="flex-1 bg-[#0A0A0A] border border-white/10 px-3 py-2 font-body text-sm text-white focus:border-[#14A89A] outline-none" />
          <button onClick={addSection} data-testid="beacon-add-section-btn" className="flex items-center gap-1.5 border border-[#14A89A]/40 text-[#14A89A] px-3 py-2 font-body text-xs hover:bg-[#14A89A]/10"><Plus size={14} /> Section</button>
        </div>
        {(beacon.sections || []).length === 0 && <p className="font-body text-sm text-zinc-500">Add a section to start organizing your work.</p>}
        {(beacon.sections || []).length > 1 && <p className="font-body text-xs text-zinc-500 mb-3">Tip: grab any section or lesson (or its <GripVertical size={12} className="inline" /> handle) and drag it into place, lessons can move into another section too. On a phone, press and hold first.</p>}
        <BeaconContentBoard sections={beacon.sections || []} onReorder={reorder}
          renderHeader={(s, si, sArr, handle) => (<>
            <div className="flex items-center justify-between mb-3 gap-2">
              <div className="flex items-center gap-1.5 min-w-0">{handle}<SectionTitle section={s} onRename={renameSection} /></div>
              <div className="flex items-center gap-1.5 shrink-0">
                <SectionCommentToggle s={s} highlight={si === 0} ensureKey={() => ensureMyBeaconKey(beacon.id)} onSaved={() => load({ keepForm: true })} />
                <button onClick={() => moveSection(s.id, "up")} disabled={si === 0} title="Move section up" data-testid={`beacon-section-up-${s.id}`} className="text-zinc-500 hover:text-white disabled:opacity-25 disabled:hover:text-zinc-500"><ChevronUp size={16} /></button>
                <button onClick={() => moveSection(s.id, "down")} disabled={si === sArr.length - 1} title="Move section down" data-testid={`beacon-section-down-${s.id}`} className="text-zinc-500 hover:text-white disabled:opacity-25 disabled:hover:text-zinc-500"><ChevronDown size={16} /></button>
                <button onClick={() => delSection(s.id)} title="Delete this section only" data-testid={`beacon-del-section-${s.id}`} className="flex items-center gap-1 text-zinc-500 hover:text-red-400 font-body text-xs ml-1"><Trash2 size={14} /> Delete section</button>
              </div>
            </div>
            <SectionDetails s={s} onSaved={() => load({ keepForm: true })} />
          </>)}
          renderItem={(it, ii, iArr, handle) => (
            <ItemRow it={it} ii={ii} iArr={iArr} handle={handle} teaserOn={beacon.public_teaser_enabled} beaconKey={beaconKey} beaconId={beacon.id} beaconHandle={beacon.handle} ensureKey={ensureMyBeaconKey}
              commentsOn={(beacon.sections || []).some((s) => s.comments && s.items.some((x) => x.id === it.id))}
              onMove={moveItem} onDelete={delItem} onSaved={() => load({ keepForm: true })} />
          )}
          renderFooter={(s) => <AddItem sectionId={s.id} teaserOn={beacon.public_teaser_enabled} beaconKey={beaconKey} beaconId={beacon.id} ensureKey={ensureMyBeaconKey} onAdded={() => load({ keepForm: true })} />} />
      </div>

      {/* Backup */}
      <div className="mt-8 border-t border-white/10 pt-6">
        <CoachTips ids={["beacon-backup"]} />
        <button onClick={downloadMine} data-testid="beacon-download-btn" className="flex items-center gap-2 border border-[#14A89A]/40 text-[#14A89A] px-5 py-2.5 font-body text-sm hover:bg-[#14A89A]/10 transition-colors">
          <Download size={15} /> Download my Beacon
        </button>
        <p className="font-body text-xs text-zinc-600 mt-2">Saves everything currently in your Beacon: title, tagline, and every section &amp; item (with media references), to a file on your device. Your work, in your hands.</p>
        <p className="font-body text-xs text-zinc-500 mt-1.5" data-testid="beacon-download-delete-note">Only what's in your Beacon right now is included. When you delete a section or piece, or replace a file, the old file is permanently removed to free your storage, and it can't be downloaded or recovered afterward. Download a copy first if you might want it later.</p>
      </div>

      {/* Danger zone */}
      <div className="mt-8 border-t border-white/10 pt-6">
        <button onClick={resetBeacon} disabled={busy} data-testid="beacon-reset-btn" className="flex items-center gap-2 border border-red-600/40 text-red-400/90 px-5 py-2.5 font-body text-sm hover:bg-red-600/15 transition-colors disabled:opacity-40">
          <Trash2 size={15} /> Delete data in this profile: start from scratch
        </button>
        <p className="font-body text-xs text-zinc-600 mt-2">Permanently erases all your Beacon content and settings and resets to a blank draft. Your creator access and link handle are kept.</p>
      </div>
    </div>
  );
}

const MediaUpload = ({ label, value, type, onDone }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef();
  const up = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", f); fd.append("enc", "false"); const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onDone(data.path); } finally { setBusy(false); }
  };
  return (
    <div className="mb-3">
      <span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-1">{label}</span>
      {value && <div className="mb-2">{type === "video" ? <BeaconMedia path={value} type="video" className="max-h-40 rounded" /> : <BeaconMedia path={value} type="image" className="max-h-32 rounded" />}</div>}
      <input ref={ref} type="file" accept={type === "video" ? "video/*" : "image/*"} onChange={up} className="hidden" />
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:bg-white/5 disabled:opacity-40">{busy ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />} {value ? "Replace" : "Upload"}</button>
    </div>
  );
};

// Banner output width; height follows the creator's chosen banner size.
const COVER_W = 1600;

const ShowTipsAgain = () => {
  const { user, resetTips } = useAuth();
  const { tips } = useTips();
  const ids = tips.filter((t) => t.screen === "beacon").map((t) => t.tip_id);
  const anyHidden = (user?.dismissed_tips || []).some((k) => ids.includes(k.split("@")[0]));
  if (!anyHidden) return null;
  return <button type="button" onClick={() => resetTips("beacon", ids)} data-testid="beacon-show-tips-again" className="font-body text-xs text-[#14A89A] hover:underline">Show tips again</button>;
};

const SaveStatus = ({ state, error, onRetry }) => (
  <div className="flex items-center gap-2 mb-3 font-body text-sm min-h-[24px]" data-testid="beacon-save-status" aria-live="polite">
    {(state === "pending" || state === "saving") && <><Loader2 className="animate-spin text-zinc-400" size={14} /><span className="text-zinc-400">Saving…</span></>}
    {state === "saved" && <><Check className="text-[#14A89A]" size={15} /><span className="text-[#14A89A]">All changes saved</span></>}
    {state === "error" && <><span className="text-red-400" data-testid="beacon-save-error">{error}</span><button type="button" onClick={onRetry} data-testid="beacon-save-retry" className="underline text-zinc-300 hover:text-white">Try again</button></>}
    {state === "idle" && <span className="text-zinc-500">Changes save automatically.</span>}
  </div>
);

const CoverUpload = ({ label, value, ratio, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [srcUrl, setSrcUrl] = useState(null);
  const fileRef = useRef();
  const pick = (e) => { const f = e.target.files?.[0]; if (!f) return; setSrcUrl(URL.createObjectURL(f)); e.target.value = ""; };
  const uploadBlob = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "cover.jpg", { type: "image/jpeg" }));
      fd.append("enc", "false");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onDone(data.path);
    } finally { setBusy(false); setSrcUrl(null); }
  };
  return (
    <div className="mb-3">
      <span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-1">{label}</span>
      <p className="font-body text-xs text-zinc-500 mb-2">Shown as a wide banner across the top of your Beacon. Reposition and zoom to fit the frame perfectly.</p>
      {value && <div className="mb-2"><BeaconMedia path={value} type="image" className="w-full object-cover rounded border border-white/10" style={{ aspectRatio: ratio }} /></div>}
      <input ref={fileRef} type="file" accept="image/*" onChange={pick} className="hidden" />
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="beacon-cover-upload" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:bg-white/5 disabled:opacity-40">{busy ? <Loader2 className="animate-spin" size={13} /> : <Upload size={13} />} {value ? "Replace banner" : "Upload banner"}</button>
      {srcUrl && <CoverCropper src={srcUrl} ratio={ratio} busy={busy} onCancel={() => setSrcUrl(null)} onConfirm={uploadBlob} />}
    </div>
  );
};

const CoverCropper = ({ src, ratio, busy, onCancel, onConfirm }) => {
  const frameRef = useRef();
  const imgRef = useRef();
  const drag = useRef(null);
  const inited = useRef(false);
  const [nat, setNat] = useState(null);
  const [frame, setFrame] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const measure = () => { if (frameRef.current) { const r = frameRef.current.getBoundingClientRect(); setFrame({ w: r.width, h: r.height }); } };
    measure(); window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const baseScale = nat && frame.w ? Math.max(frame.w / nat.w, frame.h / nat.h) : 1;
  const s = baseScale * zoom;
  const dw = nat ? nat.w * s : 0;
  const dh = nat ? nat.h * s : 0;
  const clampOff = (o, w = dw, h = dh) => ({ x: Math.min(0, Math.max(frame.w - w, o.x)), y: Math.min(0, Math.max(frame.h - h, o.y)) });

  useEffect(() => {
    if (nat && frame.w && !inited.current) {
      inited.current = true;
      setOff({ x: (frame.w - dw) / 2, y: (frame.h - dh) / 2 });
    }
  }, [nat, frame.w]); // eslint-disable-line

  const changeZoom = (nz) => {
    if (!nat) { setZoom(nz); return; }
    const s0 = baseScale * zoom, s1 = baseScale * nz;
    const cx = frame.w / 2, cy = frame.h / 2;
    const nx = cx - (cx - off.x) * (s1 / s0);
    const ny = cy - (cy - off.y) * (s1 / s0);
    setZoom(nz);
    setOff(clampOff({ x: nx, y: ny }, nat.w * s1, nat.h * s1));
  };
  const down = (e) => { drag.current = { sx: e.clientX, sy: e.clientY, ox: off.x, oy: off.y }; e.currentTarget.setPointerCapture(e.pointerId); };
  const move = (e) => { if (!drag.current) return; setOff(clampOff({ x: drag.current.ox + (e.clientX - drag.current.sx), y: drag.current.oy + (e.clientY - drag.current.sy) })); };
  const up = () => { drag.current = null; };

  const doCrop = () => {
    const canvas = document.createElement("canvas");
    canvas.width = COVER_W; canvas.height = Math.round(COVER_W / ratio);
    const ctx = canvas.getContext("2d");
    const sx = (0 - off.x) / s, sy = (0 - off.y) / s, sW = frame.w / s, sH = frame.h / s;
    ctx.drawImage(imgRef.current, sx, sy, sW, sH, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => { if (blob) onConfirm(blob); }, "image/jpeg", 0.9);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" data-testid="beacon-cover-cropper">
      <div className="bg-[#121212] border border-white/10 p-5 w-full max-w-lg">
        <h3 className="font-body text-white text-lg mb-1">Position your banner</h3>
        <p className="font-body text-xs text-zinc-500 mb-3">Drag the image to move it, and use the slider to zoom. Everything inside the frame becomes your banner.</p>
        <div ref={frameRef} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up} className="relative w-full overflow-hidden bg-black rounded touch-none cursor-move select-none" style={{ aspectRatio: ratio }}>
          <img ref={imgRef} src={src} alt="" draggable={false} onLoad={(e) => setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            className="absolute top-0 left-0 max-w-none pointer-events-none"
            style={{ width: dw || "auto", height: dh || "auto", transform: `translate(${off.x}px, ${off.y}px)` }} />
          <div className="absolute inset-0 ring-1 ring-white/20 pointer-events-none" />
        </div>
        <div className="flex items-center gap-3 mt-4">
          <span className="font-body text-xs text-zinc-500 uppercase tracking-[0.15em]">Zoom</span>
          <input type="range" min="1" max="4" step="0.01" value={zoom} onChange={(e) => changeZoom(parseFloat(e.target.value))} data-testid="beacon-cover-zoom" className="flex-1 accent-[#14A89A]" />
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button onClick={onCancel} disabled={busy} className="border border-white/15 text-zinc-300 px-4 py-2 font-body text-sm hover:bg-white/5 disabled:opacity-40">Cancel</button>
          <button onClick={doCrop} disabled={busy || !nat} data-testid="beacon-cover-crop-confirm" className="flex items-center gap-1.5 bg-[#14A89A] text-[#0A0A0A] font-semibold px-5 py-2 font-body text-sm hover:brightness-110 disabled:opacity-40">{busy ? <><Loader2 className="animate-spin" size={15} /> Saving…</> : <><Check size={15} /> Use this banner</>}</button>
        </div>
      </div>
    </div>
  );
};

const InlineEdit = ({ value, placeholder, onSave, className, inputClassName, style, testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { if (!editing) setDraft(value || ""); }, [value, editing]);
  const commit = () => { setEditing(false); if ((draft || "").trim() !== (value || "")) onSave((draft || "").trim()); };
  if (editing) return (
    <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }}
      data-testid={testid} className={inputClassName} style={style} />
  );
  return (
    <span onClick={() => setEditing(true)} data-testid={testid ? `${testid}-display` : undefined} title="Click to edit"
      role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEditing(true); } }}
      className={`group inline-flex items-center gap-1.5 cursor-text hover:opacity-90 ${className || ""}`} style={style}>
      {value ? <span className="min-w-0 break-words">{value}</span> : <span className="opacity-40">{placeholder}</span>}
      <Pencil size={13} className="opacity-0 group-hover:opacity-60 transition-opacity shrink-0" style={{ color: "#14A89A" }} />
    </span>
  );
};

// Cover picture + short description shown on the viewer's section card.
const SectionDetails = ({ s, onSaved }) => {
  const [desc, setDesc] = useState(s.description || "");
  useEffect(() => { setDesc(s.description || ""); }, [s.description]);
  const patch = async (body) => { try { await api.patch(`/beacon/mine/sections/${s.id}`, body); await onSaved(); } catch { window.alert("Couldn't save, please try again."); } };
  return (
    <div className="grid sm:grid-cols-[150px_1fr] gap-3 mb-3 pb-3 border-b border-white/5" onPointerDown={(e) => e.stopPropagation()} data-testid={`beacon-section-details-${s.id}`}>
      <div>
        <MediaUpload label="Section cover (optional)" value={s.cover} type="image" onDone={(p) => patch({ cover: p || "" })} />
        {s.cover && <button type="button" onClick={() => patch({ cover: "" })} data-testid={`beacon-section-cover-remove-${s.id}`} className="mt-1 flex items-center gap-1 font-body text-[11px] text-zinc-500 hover:text-red-400"><Trash2 size={11} /> Remove cover</button>}
      </div>
      <label className="block">
        <span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500">Short description (optional)</span>
        <input value={desc} maxLength={160} onChange={(e) => setDesc(e.target.value)} onBlur={() => { if (desc !== (s.description || "")) patch({ description: desc }); }}
          placeholder="e.g. Begin here: the foundations" data-testid={`beacon-section-desc-input-${s.id}`}
          className="mt-1 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 font-body text-sm text-white focus:border-[#14A89A] outline-none" />
        <span className="font-body text-[11px] text-zinc-600">Shown under the title on your section card. No cover? A tidy colour tile is used.</span>
        <span className="block mt-3 font-body text-xs tracking-[0.15em] uppercase text-zinc-500">Layout</span>
        <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`beacon-section-layout-${s.id}`}>
          {SECTION_LAYOUTS.map(([v, l]) => (
            <button key={v} type="button" onClick={() => patch({ layout: v })} data-testid={`beacon-section-layout-${s.id}-${v}`}
              className={`border px-2.5 py-1 font-body text-xs transition-colors ${(s.layout || "list") === v ? "border-[#14A89A] text-white bg-[#14A89A]/10" : "border-white/10 text-zinc-400 hover:border-white/25"}`}>{l}</button>
          ))}
        </div>
        <span className="font-body text-[11px] text-zinc-600">Grid, Masonry and Single column show Artwork pieces as a gallery. Course: Path unlocks lessons one after another; Course: Library lets students take lessons in any order.</span>
      </label>
      <div className="mt-3" data-testid={`beacon-section-course-${s.id}`}>
        <span className="block font-body text-xs tracking-[0.15em] uppercase text-zinc-500 mb-1">Course details (optional)</span>
        <div className="flex flex-wrap gap-2">
        <select value={s.level || ""} onChange={(e) => patch({ level: e.target.value })} aria-label="Level" data-testid={`beacon-section-level-${s.id}`} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-xs text-white">
          {LEVELS.map(([v, l]) => <option key={v} value={v}>{v ? l : "Level: none"}</option>)}
        </select>
        <input defaultValue={s.est_time || ""} onBlur={(e) => e.target.value !== (s.est_time || "") && patch({ est_time: e.target.value })} maxLength={40} placeholder="Estimated time, e.g. 3 hours" aria-label="Estimated time" data-testid={`beacon-section-time-${s.id}`}
          className="flex-1 min-w-[160px] bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-xs text-white" />
        </div>
      </div>
    </div>
  );
};

const SectionTitle = ({ section, onRename }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(section.title || "");
  const save = async () => {
    const t = draft.trim();
    if (!t || t === section.title) { setEditing(false); return; }
    await onRename(section.id, t);
    setEditing(false);
  };
  if (editing) return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={120}
        onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(section.title || ""); setEditing(false); } }}
        data-testid={`beacon-section-title-input-${section.id}`}
        className="flex-1 min-w-0 bg-[#0A0A0A] border border-[#14A89A]/50 px-2 py-1.5 font-body text-sm text-white outline-none" />
      <button onClick={save} data-testid={`beacon-section-title-save-${section.id}`} className="text-[#14A89A] hover:brightness-110"><Check size={16} /></button>
      <button onClick={() => { setDraft(section.title || ""); setEditing(false); }} title="Cancel" data-testid={`beacon-section-title-cancel-${section.id}`} className="text-zinc-500 hover:text-white font-body text-sm px-1">✕</button>
    </div>
  );
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className="font-body text-white font-medium truncate">{section.title}</span>
      <button onClick={() => { setDraft(section.title || ""); setEditing(true); }} title="Rename section" data-testid={`beacon-section-edit-${section.id}`} className="text-zinc-600 hover:text-[#14A89A] shrink-0"><Pencil size={13} /></button>
    </div>
  );
};

const FREE_NOTE = "Its images, videos and files are permanently removed to free your storage. Download my Beacon only includes what's currently in your Beacon, so deleted work can't be downloaded or recovered later.";

const EMPTY_ITEM = { type: "text", title: "", body: "", media: "", caption: "", link_url: "", link_label: "", link_thumb: "", is_preview: false, is_public: false, session: { url: "", when: "" } };

// Media picker for a Beacon item. New picks are held locally and uploaded on save
// (so we can encrypt with the final preview/public decision). Existing media renders
// via BeaconMedia, decrypting on-device when the item is encrypted.
const ItemMediaPicker = ({ type, existingPath, enc, beaconKey, mediaIv, previewUrl, onPick }) => {
  const ref = useRef();
  return (
    <div className="mb-1">
      <span className="font-body text-xs tracking-[0.15em] uppercase text-zinc-500 block mb-1">{type} file</span>
      {previewUrl && (type === "image"
        ? <img src={previewUrl} alt="" className="max-h-32 rounded mb-2" />
        : type === "video" ? <video src={previewUrl} controls controlsList="nodownload" className="max-h-40 rounded mb-2" /> : <audio src={previewUrl} controls controlsList="nodownload" className="w-full mb-2" />)}
      {!previewUrl && existingPath && <div className="mb-2"><BeaconMedia path={existingPath} type={type} encKey={enc ? beaconKey : null} iv={mediaIv} className={type === "image" ? "max-h-32 rounded" : "max-h-40 rounded"} /></div>}
      <input ref={ref} type="file" accept={type === "image" ? "image/*" : type === "audio" ? "audio/*" : "video/*"} onChange={(e) => { const file = e.target.files?.[0]; if (file) onPick(file); e.target.value = ""; }} className="hidden" />
      <button type="button" onClick={() => ref.current?.click()} data-testid="beacon-item-media-upload" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-1.5 font-body text-xs hover:bg-white/5"><Upload size={13} /> {existingPath || previewUrl ? "Replace" : "Upload"}</button>
      {existingPath && <p className="font-body text-[11px] text-zinc-600 mt-1">Replacing permanently removes the old file once you save.</p>}
    </div>
  );
};

const ItemForm = ({ sectionId, itemId, initial, teaserOn, beaconKey, beaconId, ensureKey, onSaved, onCancel }) => {
  const [f, setF] = useState(() => ({
    ...EMPTY_ITEM, ...(initial || {}),
    session: (initial && initial.session) || { url: "", when: "" },
    caption: (initial && initial.caption) || "", link_url: (initial && initial.link_url) || "",
    link_label: (initial && initial.link_label) || "", link_thumb: (initial && initial.link_thumb) || "",
    drip: initial?.drip || null,
  }));
  const [art, setArt] = useState(() => (initial?.type === "artwork" && !initial.enc ? parseArt(initial.body) : { ...EMPTY_ART }));
  const [busy, setBusy] = useState(false);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [extras, setExtras] = useState(() => ({ voice: null, video: null, process: [], ...(initial?.extras || {}) }));
  const [exFiles, setExFiles] = useState({ voice: null, video: null, process: [] });
  const [fileMeta, setFileMeta] = useState(null);

  // Editing an encrypted item: decrypt caption/body on-device for editing.
  useEffect(() => {
    (async () => {
      if (!initial || !initial.enc || !beaconKey) return;
      const patch = {};
      try { if (initial.caption && initial.caption_iv) patch.caption = await C.decryptText(beaconKey, initial.caption, initial.caption_iv); } catch { /* keep */ }
      try { if (initial.type === "text" && initial.body && initial.body_iv) patch.body = await C.decryptText(beaconKey, initial.body, initial.body_iv); } catch { /* keep */ }
      try { if (initial.type === "artwork" && initial.body && initial.body_iv) setArt(parseArt(await C.decryptText(beaconKey, initial.body, initial.body_iv))); } catch { /* keep */ }
      try { if (initial.type === "file" && initial.body) setFileMeta(JSON.parse(initial.body_iv ? await C.decryptText(beaconKey, initial.body, initial.body_iv) : initial.body)); } catch { /* keep */ }
      if (Object.keys(patch).length) setF((prev) => ({ ...prev, ...patch }));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beaconKey]);

  const artPx = ART_QUALITY[art.quality] || ART_QUALITY.standard;
  const plainUpload = async (file, type = f.type) => {
    if (type === "artwork" || type === "process") file = await compressImage(file, type === "artwork" ? artPx : 2500);
    const fd = new FormData(); fd.append("file", file); fd.append("enc", "false");
    const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return { path: data.path, iv: null, thumb: null, thumb_iv: null };
  };
  const upload = (file, itemEnc, key, type = f.type) => (itemEnc
    ? encryptAndUploadWithKey(file, (b) => C.encryptBytes(key, b), type === "process" ? "image" : type, undefined, type === "artwork" ? artPx : 2500)
    : plainUpload(file, type));
  const reconcileMedia = async (path, wasEnc, toEnc, key, type = f.type, iv = initial?.media_iv) => {
    const r = await api.get(`/files/${path}`, { responseType: "blob" });
    const mime = MIME_FOR[type === "process" ? "image" : type] || "application/octet-stream";
    let file;
    if (wasEnc) {
      const buf = await r.data.arrayBuffer();
      const plainBlob = C.isChunkedMedia(buf)
        ? await C.decryptChunkedToBlob(key, buf, mime)
        : new Blob([await C.decryptBytes(key, buf, iv)], { type: mime });
      file = new File([plainBlob], "m", { type: mime });
    } else {
      file = new File([r.data], "m", { type: mime });
    }
    return upload(file, toEnc, key, type);
  };
  const resolveMedia = async (itemEnc, key) => {
    if (mediaFile) return upload(f.type === "artwork" && art.wm?.on && (art.wm.text || "").trim() ? await applyWatermark(mediaFile, artPx, art.wm) : mediaFile, itemEnc, key);
    if (!f.media) return { path: null, iv: null, thumb: null, thumb_iv: null };
    const existingEnc = !!initial?.enc;
    if (existingEnc === itemEnc) return { path: f.media, iv: initial?.media_iv || null, thumb: initial?.thumb || null, thumb_iv: initial?.thumb_iv || null };
    return await reconcileMedia(f.media, existingEnc, itemEnc, key);
  };
  // Story extras: keep, re-key (if privacy changed) or upload each voice note, video and process image.
  const resolveExtras = async (itemEnc, key) => {
    const wasEnc = !!initial?.enc;
    const keep = async (m, type) => (wasEnc === itemEnc ? m : await reconcileMedia(m.path, wasEnc, itemEnc, key, type, m.iv));
    const pick = (r) => ({ path: r.path, iv: r.iv || null });
    const one = async (file, m, type) => (file ? pick(await upload(file, itemEnc, key, type)) : m ? pick(await keep(m, type)) : null);
    const process = [];
    for (const m of extras.process || []) process.push(pick(await keep(m, "process")));
    for (const file of exFiles.process) process.push(pick(await upload(file, itemEnc, key, "process")));
    return { voice: await one(exFiles.voice, extras.voice, "audio"), video: await one(exFiles.video, extras.video, "video"), process: process.slice(0, 8) };
  };

  const save = async () => {
    setBusy(true);
    try {
      const wantEnc = !f.is_public && !f.is_preview;
      // Fetch the Beacon key on demand so we never silently downgrade an
      // intended-encrypted item to plaintext because the key wasn't ready.
      let key = beaconKey;
      if (wantEnc && !key && ensureKey && beaconId) {
        try { key = await ensureKey(beaconId); } catch (e) { console.error("ensureKey failed", e); }
      }
      if (wantEnc && !key) {
        window.alert("Your Beacon encryption key isn't ready yet. Please wait a moment and try again, we won't save this as readable.");
        return;
      }
      const itemEnc = wantEnc && !!key;
      const payload = {
        type: f.type, title: f.title, duration: f.duration || "",
        link_url: f.link_url, link_label: f.link_label, link_thumb: f.link_thumb,
        is_preview: f.is_preview, is_public: f.is_public, enc: itemEnc,
        drip: f.type === "artwork" ? {} : (f.drip || {}),
      };
      if (itemEnc && f.caption) { const e = await C.encryptText(key, f.caption); payload.caption = e.ctB64; payload.caption_iv = e.ivB64; }
      else { payload.caption = f.caption; payload.caption_iv = null; }
      if (f.type === "artwork") {
        if (!mediaFile && !f.media) { window.alert("Add an image for this artwork first."); return; }
        const raw = JSON.stringify(art);
        if (itemEnc) { const e = await C.encryptText(key, raw); payload.body = e.ctB64; payload.body_iv = e.ivB64; }
        else { payload.body = raw; payload.body_iv = null; }
        payload.extras = await resolveExtras(itemEnc, key);
      }
      if (f.type === "text") {
        if (itemEnc && f.body) { const e = await C.encryptText(key, f.body); payload.body = e.ctB64; payload.body_iv = e.ivB64; }
        else { payload.body = f.body; payload.body_iv = null; }
      } else if (f.type === "session") payload.session = f.session;
      if (["image", "video", "audio", "artwork", "file"].includes(f.type)) {
        if (f.type === "file" && !mediaFile && !f.media && !(f.link_url || "").trim()) { window.alert("Choose a file to share, or paste a Proton Drive share link."); return; }
        const m = await resolveMedia(itemEnc, key);
        payload.media = m.path; payload.media_iv = m.iv || null; payload.thumb = m.thumb || null; payload.thumb_iv = m.thumb_iv || null;
        if (f.type === "file") {
          const prev = fileMeta || {};
          const meta = JSON.stringify(mediaFile ? { name: mediaFile.name, mime: mediaFile.type || "application/octet-stream" } : { name: prev.name || initial?.file_name || "", mime: prev.mime || initial?.file_mime || "" });
          if (itemEnc) { const e = await C.encryptText(key, meta); payload.body = e.ctB64; payload.body_iv = e.ivB64; } else { payload.body = meta; payload.body_iv = null; }
          payload.file_name = ""; payload.file_mime = "";
        }
      }
      if (itemId) await api.patch(`/beacon/mine/items/${itemId}`, payload);
      else await api.post("/beacon/mine/items", { section_id: sectionId, ...payload });
      await onSaved();
    } catch (e) { window.alert(e?.response?.data?.detail || "Couldn't save this item. Please try again."); }
    finally { setBusy(false); }
  };
  const willEncrypt = !f.is_public && !f.is_preview;
  return (
    <div className="mt-3 border-t border-white/5 pt-3 space-y-2" data-testid={itemId ? `beacon-item-edit-form-${itemId}` : "beacon-item-add-form"}>
      <div className="flex gap-2 flex-wrap">
        <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="beacon-item-type" className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white">
          {ITEM_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title (optional)" data-testid="beacon-item-title" className="flex-1 bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" />
        {f.type !== "artwork" && <input value={f.duration || ""} onChange={(e) => setF({ ...f, duration: e.target.value })} placeholder="Length, e.g. 12 min" maxLength={20} data-testid="beacon-item-duration" className="w-32 bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" />}
      </div>
      {f.type !== "artwork" && !f.is_preview && <DripFields drip={f.drip} onChange={(drip) => setF({ ...f, drip })} />}
      <textarea value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} rows={2} placeholder="Caption (optional)" data-testid="beacon-item-caption" className="w-full bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" />
      {f.type === "text" && <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={3} placeholder="Lesson text" className="w-full bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" />}
      {f.type === "session" && <div className="flex gap-2"><input value={f.session.url} onChange={(e) => setF({ ...f, session: { ...f.session, url: e.target.value } })} placeholder="Proton Meet link" className="flex-1 bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" /><input type="datetime-local" value={f.session.when} onChange={(e) => setF({ ...f, session: { ...f.session, when: e.target.value } })} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" /></div>}
      {f.type === "file" && (
        <label className="block border border-white/8 bg-[#0A0A0A]/60 p-2.5" data-testid="beacon-item-file-field">
          <span className="font-body text-xs text-zinc-400">{mediaFile ? `Selected: ${mediaFile.name}` : (fileMeta?.name || initial?.file_name) ? `Current file: ${fileMeta?.name || initial.file_name} (choose another to replace it)` : "Choose a PDF, worksheet, slides or any file students can download"}</span>
          <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.rtf,.epub,.zip,.key,.pages,.numbers,image/*,audio/*" data-testid="beacon-item-file-input"
            onChange={(e) => { const file = e.target.files?.[0]; if (file) setMediaFile(file); e.target.value = ""; }} className="block mt-1.5 font-body text-xs text-zinc-300" />
        </label>
      )}
      {["video", "audio", "image", "artwork"].includes(f.type) && (
        <ItemMediaPicker type={f.type === "artwork" ? "image" : f.type} existingPath={mediaFile ? null : f.media} enc={!!initial?.enc} beaconKey={beaconKey} mediaIv={initial?.media_iv}
          previewUrl={mediaPreview} onPick={(file) => { setMediaFile(file); setMediaPreview(URL.createObjectURL(file)); }} />
      )}
      {f.type === "artwork" && <ArtworkFields art={art} onChange={setArt} />}
      {f.type === "artwork" && <WatermarkFields art={art} onChange={setArt} file={mediaFile} isPublic={f.is_public || f.is_preview} />}
      {f.type === "artwork" && <ArtworkStoryFields art={art} onArt={setArt} extras={extras} onExtras={setExtras} files={exFiles} onFiles={setExFiles} />}

      {/* Optional external website link (the primary content for a "Website link" item) */}
      <div className="border border-white/8 bg-[#0A0A0A]/60 p-2.5 space-y-2">
        <div className="flex items-center gap-1.5 text-zinc-400 font-body text-[11px] uppercase tracking-wider"><Link2 size={12} /> {f.type === "link" ? "Website" : "Optional website link"}</div>
        <input value={f.link_url} onChange={(e) => setF({ ...f, link_url: e.target.value })} placeholder="https://your-website.com" data-testid="beacon-item-link-url" className="w-full bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" />
        <input value={f.link_label} onChange={(e) => setF({ ...f, link_label: e.target.value })} placeholder="Link label (optional, e.g. Visit my shop)" data-testid="beacon-item-link-label" className="w-full bg-[#0A0A0A] border border-white/10 px-2 py-1.5 font-body text-sm text-white" />
        <MediaUpload label="Link logo / thumbnail (optional)" value={f.link_thumb} type="image" onDone={(p) => setF({ ...f, link_thumb: p })} />
        {["link", "file"].includes(f.type) && (
          <p className="font-body text-[11px] text-zinc-500 leading-relaxed" data-testid="beacon-item-drive-tip">
            Hosting a big file? Upload it to Proton Drive, create a share link and paste it here{f.type === "file" ? " instead of choosing a file" : ""}.{" "}
            <a href={PROTON_DRIVE_AFFILIATE_URL} target="_blank" rel="noopener noreferrer" data-testid="beacon-item-drive-link" className="text-[#14A89A] hover:underline">Get Proton Drive</a>
          </p>
        )}
      </div>

      <div className="flex gap-4 items-center flex-wrap">
        <label className="flex items-center gap-1.5 text-xs text-zinc-400"><input type="checkbox" checked={f.is_preview} onChange={(e) => setF({ ...f, is_preview: e.target.checked })} className="accent-[#14A89A]" /> Free preview</label>
        {teaserOn && <label className="flex items-center gap-1.5 text-xs text-zinc-400"><input type="checkbox" checked={f.is_public} onChange={(e) => setF({ ...f, is_public: e.target.checked })} className="accent-[#14A89A]" /> Show on public teaser</label>}
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider ${willEncrypt ? (beaconKey ? "text-[#14A89A]" : "text-amber-400/80") : "text-amber-400/80"}`} data-testid="beacon-item-enc-badge">
          {willEncrypt ? (beaconKey ? <><Lock size={10} /> Encrypted</> : <><Loader2 size={10} className="animate-spin" /> Preparing key…</>) : <><Globe size={10} /> Readable to non-subscribers</>}
        </span>
        <button onClick={save} disabled={busy} data-testid="beacon-item-save" className="ml-auto bg-[#14A89A] text-[#0A0A0A] font-semibold px-3 py-1.5 font-body text-xs disabled:opacity-40">{busy ? "…" : itemId ? "Save" : "Add"}</button>
        <button onClick={onCancel} className="text-zinc-500 text-xs">Cancel</button>
      </div>
    </div>
  );
};

const ItemRow = ({ it, ii, iArr, handle, teaserOn, beaconKey, beaconId, beaconHandle, ensureKey, commentsOn, onMove, onDelete, onSaved }) => {
  const [editing, setEditing] = useState(false);
  if (editing) return (
    <div className="border-t border-white/5">
      <ItemForm itemId={it.id} initial={it} teaserOn={teaserOn} beaconKey={beaconKey} beaconId={beaconId} ensureKey={ensureKey} onSaved={async () => { setEditing(false); await onSaved(); }} onCancel={() => setEditing(false)} />
    </div>
  );
  const label = it.title || (it.type === "link" ? (it.link_label || it.link_url || "Website link") : "Untitled");
  return (
    <div className="flex items-center gap-2 py-1.5 text-sm border-t border-white/5" data-testid={`beacon-item-row-${it.id}`}>
      {handle}
      <span className="text-zinc-500 text-xs uppercase tracking-wide w-16 shrink-0">{it.type}</span>
      <span className={`flex-1 truncate ${it.title ? "text-zinc-200" : "text-zinc-500 italic"}`}>{label}</span>
      {it.enc && <Lock size={11} className="text-[#14A89A] shrink-0" title="Encrypted for subscribers" />}
      {it.link_url && <Link2 size={12} className="text-[#14A89A] shrink-0" />}
      {it.is_preview && <span className="text-[10px] text-[#14A89A] shrink-0"><Eye size={11} className="inline" /> preview</span>}
      {it.drip && !it.is_preview && <span className="text-[10px] text-amber-300/90 shrink-0" data-testid={`beacon-item-drip-badge-${it.id}`}>{dripLabel(it.drip)}</span>}
      {it.is_public && <span className="text-[10px] text-amber-400/80 shrink-0"><Globe size={11} className="inline" /> public</span>}
      {commentsOn && <ItemCommentToggle it={it} onSaved={onSaved} />}
      <ShareToHearth it={it} handle={beaconHandle} />
      <button onClick={() => onMove(it.id, "up")} disabled={ii === 0} title="Move up" data-testid={`beacon-item-up-${it.id}`} className="text-zinc-600 hover:text-white disabled:opacity-25 disabled:hover:text-zinc-600"><ChevronUp size={14} /></button>
      <button onClick={() => onMove(it.id, "down")} disabled={ii === iArr.length - 1} title="Move down" data-testid={`beacon-item-down-${it.id}`} className="text-zinc-600 hover:text-white disabled:opacity-25 disabled:hover:text-zinc-600"><ChevronDown size={14} /></button>
      <button onClick={() => setEditing(true)} title="Edit item" data-testid={`beacon-item-edit-${it.id}`} className="text-zinc-600 hover:text-[#14A89A]"><Pencil size={13} /></button>
      <button onClick={() => onDelete(it.id)} title="Delete item" data-testid={`beacon-item-del-${it.id}`} className="text-zinc-600 hover:text-red-400"><Trash2 size={13} /></button>
    </div>
  );
};

const AddItem = ({ sectionId, teaserOn, beaconKey, beaconId, ensureKey, onAdded }) => {
  const [open, setOpen] = useState(false);
  if (!open) return <button onClick={() => setOpen(true)} data-testid={`beacon-add-item-${sectionId}`} className="mt-2 flex items-center gap-1.5 text-[#14A89A] font-body text-xs hover:underline"><Plus size={13} /> Add item</button>;
  return <ItemForm sectionId={sectionId} teaserOn={teaserOn} beaconKey={beaconKey} beaconId={beaconId} ensureKey={ensureKey} onSaved={async () => { setOpen(false); await onAdded(); }} onCancel={() => setOpen(false)} />;
};

const CopyTeaser = ({ handle }) => {
  const [done, setDone] = useState(false);
  const link = `${window.location.origin}/b/${handle}`;
  return (
    <button onClick={() => { navigator.clipboard.writeText(link); setDone(true); setTimeout(() => setDone(false), 1500); }} data-testid="beacon-copy-teaser" className="mt-3 ml-3 inline-flex items-center gap-1.5 text-zinc-400 font-body text-xs hover:text-white">
      <Copy size={13} /> {done ? "Copied!" : "Copy public teaser link"}
    </button>
  );
};

const BeaconBrowse = ({ dir, nav }) => (
  <div className="mt-10">
    <h2 className="font-body text-lg text-white mb-4">Explore Beacons</h2>
    <div className="grid sm:grid-cols-2 gap-3" data-testid="beacon-directory">
      {dir.map((b) => (
        <button key={b.id} onClick={() => nav(`/beacon/view/${b.handle}`)} className="text-left border border-white/10 bg-[#121212] p-4 hover:border-[#14A89A]/40 transition" data-testid={`beacon-dir-${b.handle}`}>
          <div className="font-body text-white font-medium">{b.title}</div>
          <div className="font-body text-xs text-zinc-500 mt-0.5">{b.tagline}</div>
          <div className="font-body text-xs text-[#14A89A] mt-2">{b.price_monthly > 0 ? `$${b.price_monthly}/mo` : "Free"}</div>
        </button>
      ))}
    </div>
  </div>
);
